import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, Switch, Button, App, Divider, Segmented, Typography, Collapse, Alert, Row, Col, Flex, Tooltip, theme } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import apiClient from '../api/client';

const { Text } = Typography;

interface ProxyConfig {
  protocol: string;
  host: string;
  port: number;
  login?: string;
  password?: string;
  hasPassword?: boolean; // returned by API instead of password
  label?: string;
  country?: string;
  meta?: Record<string, unknown>;
}

interface Server {
  key: string;
  name: string;
  mainIP: string;
  networks: string[];
  proxies: ProxyConfig[];
  checkMode: string;
  type?: 'dedicated' | 'mobile-gateway';
  country: string;
  tags: string[];
  enabled: boolean;
  sourceKey?: string;
  sourceId?: string;
  sourceUrl?: string;
  meta?: Record<string, unknown>;
}

interface Props {
  open: boolean;
  server?: Server | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const ServerFormModal: React.FC<Props> = ({ open, server, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  // Meta is a free-form JSON map edited as raw text so the admin can paste
  // whatever the source system sent. Parsed on submit — stays as a string in
  // local state so the user can see syntax errors before we try to save.
  const [metaText, setMetaText] = useState('');
  const [metaError, setMetaError] = useState<string>('');
  const isEdit = !!server;
  const serverType = Form.useWatch('type', form) as 'dedicated' | 'mobile-gateway' | undefined;
  const isMobile = serverType === 'mobile-gateway';

  useEffect(() => {
    if (open && server) {
      form.setFieldsValue({
        name: server.name,
        mainIP: server.mainIP,
        networks: server.networks?.length ? server.networks : [''],
        proxies: server.proxies?.length ? server.proxies : [],
        checkMode: server.checkMode,
        type: server.type || 'dedicated',
        country: server.country,
        tags: server.tags,
        enabled: server.enabled,
        sourceKey: server.sourceKey,
        sourceId: server.sourceId,
        sourceUrl: server.sourceUrl,
      });
      setMetaText(server.meta && Object.keys(server.meta).length > 0 ? JSON.stringify(server.meta, null, 2) : '');
      setMetaError('');
    } else if (open) {
      form.resetFields();
      setMetaText('');
      setMetaError('');
    }
  }, [open, server, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const filteredProxies = (values.proxies || []).filter((p: any) => p && p.host && p.port);

      // Validate: login and password must both be present or both absent
      for (let i = 0; i < filteredProxies.length; i++) {
        const p = filteredProxies[i];
        if ((p.login && !p.password) || (!p.login && p.password)) {
          message.error(`Proxy ${i + 1}: both login and password are required for authenticated proxies`);
          setLoading(false);
          return;
        }
      }

      // Validate: no duplicate host:port proxies
      const seen = new Set<string>();
      for (let i = 0; i < filteredProxies.length; i++) {
        const key = `${filteredProxies[i].host}:${filteredProxies[i].port}`;
        if (seen.has(key)) {
          message.error(`Proxy ${i + 1}: duplicate proxy ${key}`);
          setLoading(false);
          return;
        }
        seen.add(key);
      }

      // Parse meta JSON. Empty = omit entirely (keeps backend update semantics
      // of "don't touch"); non-object payloads are rejected early so we don't
      // ship a malformed request.
      let meta: Record<string, unknown> | undefined;
      const trimmed = metaText.trim();
      if (trimmed !== '') {
        try {
          const parsed = JSON.parse(trimmed);
          if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
            setMetaError('Meta must be a JSON object');
            message.error('Meta must be a JSON object');
            setLoading(false);
            return;
          }
          meta = parsed as Record<string, unknown>;
          setMetaError('');
        } catch (e: any) {
          setMetaError(e.message);
          message.error(`Meta JSON invalid: ${e.message}`);
          setLoading(false);
          return;
        }
      }

      const payload: Record<string, unknown> = {
        ...values,
        networks: (values.networks || []).filter((n: string) => n && n.trim()),
        proxies: filteredProxies,
      };
      // Only include meta if the user put something there, so create/update
      // semantics stay predictable: absent = don't touch on update, create
      // without meta leaves the field unset.
      if (meta !== undefined) {
        payload.meta = meta;
      } else if (isEdit && server?.meta && Object.keys(server.meta).length > 0) {
        // User cleared the meta box on an existing server — send empty object
        // to explicitly wipe it (backend treats {} as "replace with empty").
        payload.meta = {};
      }

      if (isEdit) {
        await apiClient.put(`/servers/${server!.key}`, payload);
        message.success('Server updated');
      } else {
        await apiClient.post('/servers', payload);
        message.success('Server created');
      }
      onSuccess();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Laid out for a 1280x800 screen: short fields share a row, the proxy row is
  // one line, and the submit button sits in the modal footer where it is
  // always visible instead of below a scroll.
  return (
    <Modal
      title={isEdit ? 'Edit Server' : 'Add Server'}
      open={open}
      onCancel={onCancel}
      destroyOnHidden
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
          {isEdit ? 'Update' : 'Create'}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ checkMode: 'active-probing', type: 'dedicated', enabled: true, networks: [''], proxies: [] }}
      >
        <Row gutter={12}>
          <Col flex="none">
            <Form.Item
              name="type"
              label="Type"
              tooltip="Dedicated = a regular proxy server. Mobile gateway = a relay whose proxies tunnel out through independent modems."
            >
              <Segmented
                options={[
                  { label: 'Dedicated', value: 'dedicated' },
                  { label: 'Mobile gateway', value: 'mobile-gateway' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col flex="200px">
            <Form.Item name="checkMode" label="Check Mode">
              <Select>
                <Select.Option value="active-probing">Active Probing</Select.Option>
                <Select.Option value="agent-heartbeat">Agent Heartbeat</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col flex="none">
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input placeholder="proxy-us-east-1" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="mainIP"
              label={isMobile ? 'Gateway IP (relay)' : 'Main IP'}
              rules={[{ required: true, message: 'IP is required' }]}
              extra={isMobile ? 'Front machine; each modem tunnels out via its own carrier.' : undefined}
            >
              <Input placeholder="10.0.0.1" />
            </Form.Item>
          </Col>
        </Row>

        <Form.List name="networks">
          {(fields, { add, remove }) => (
            <Form.Item label="Networks">
              <Flex wrap gap={8} align="center">
                {fields.map((field) => (
                  <Flex key={field.key} gap={2} align="center">
                    <Form.Item {...field} noStyle>
                      <Input placeholder="192.168.1.0/24" style={{ width: 170 }} />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Tooltip title="Remove network">
                        <Button
                          type="text"
                          size="small"
                          icon={<MinusCircleOutlined />}
                          aria-label="Remove network"
                          onClick={() => remove(field.name)}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Add Network
                </Button>
              </Flex>
            </Form.Item>
          )}
        </Form.List>

        <Form.List name="proxies">
          {(fields, { add, remove }) => (
            <Form.Item label={isMobile ? 'Modems (proxies)' : 'Proxies'}>
              {fields.map((field, index) => (
                <Flex
                  key={field.key}
                  wrap
                  gap={4}
                  align="center"
                  style={{
                    marginBottom: 6,
                    paddingBottom: 6,
                    borderBottom: `1px dashed ${token.colorBorderSecondary}`,
                  }}
                >
                  {isMobile && (
                    <Text type="secondary" style={{ fontSize: 12, width: 20 }}>#{index + 1}</Text>
                  )}
                  <Form.Item
                    name={[field.name, 'protocol']}
                    noStyle
                    rules={[{ required: true, message: 'Protocol required' }]}
                  >
                    <Select placeholder="Protocol" style={{ width: 96 }}>
                      <Select.Option value="http">HTTP</Select.Option>
                      <Select.Option value="https">HTTPS</Select.Option>
                      <Select.Option value="socks5">SOCKS5</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'host']}
                    noStyle
                    rules={[{ required: true, message: 'Host required' }]}
                  >
                    <Input placeholder="Host" style={{ width: 124 }} />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'port']}
                    noStyle
                    rules={[{ required: true, message: 'Port required' }]}
                  >
                    <InputNumber placeholder="Port" min={1} max={65535} style={{ width: 76 }} />
                  </Form.Item>
                  <Form.Item name={[field.name, 'login']} noStyle>
                    <Input placeholder="Login" style={{ width: 88 }} />
                  </Form.Item>
                  <Form.Item name={[field.name, 'password']} noStyle>
                    <Input.Password placeholder="Password" style={{ width: 104 }} />
                  </Form.Item>
                  <Form.Item name={[field.name, 'label']} noStyle>
                    <Input placeholder={isMobile ? 'Label, e.g. Orange SIM 1' : 'Label'} style={{ flex: '1 1 110px', minWidth: 110 }} />
                  </Form.Item>
                  {isMobile && (
                    <Form.Item name={[field.name, 'country']} noStyle>
                      <Input
                        placeholder="CC"
                        style={{ width: 52 }}
                        maxLength={2}
                        title="Country, 2-letter ISO — leave empty to auto-detect"
                        aria-label="Country (2-letter ISO, empty = auto-detect)"
                      />
                    </Form.Item>
                  )}
                  <Tooltip title={isMobile ? 'Remove modem' : 'Remove proxy'}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<MinusCircleOutlined />}
                      aria-label={isMobile ? `Remove modem ${index + 1}` : `Remove proxy ${index + 1}`}
                      onClick={() => remove(field.name)}
                    />
                  </Tooltip>
                </Flex>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ protocol: 'socks5', host: '', port: 1080 })}
                icon={<PlusOutlined />}
              >
                {isMobile ? 'Add Modem' : 'Add Proxy'}
              </Button>
            </Form.Item>
          )}
        </Form.List>

        <Form.Item name="tags" label="Tags">
          <Select mode="tags" placeholder="Add tags" />
        </Form.Item>

        <Divider orientation="left" plain style={{ margin: '8px 0 12px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Source / Integration (optional)</Text>
        </Divider>

        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="sourceKey" label="Source Key">
              <Input placeholder="e.g. sysboard" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="sourceId" label="Source ID">
              <Input placeholder="External ID" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="sourceUrl" label="Source URL">
              <Input placeholder="https://sysboard.example.com/server/123" />
            </Form.Item>
          </Col>
        </Row>

        <Collapse
          ghost
          size="small"
          items={[{
            key: 'meta',
            label: (
              <Text>
                Meta <Text type="secondary" style={{ fontSize: 12 }}>— raw JSON metadata from the source system</Text>
              </Text>
            ),
            children: (
              <>
                <Input.TextArea
                  value={metaText}
                  onChange={(e) => {
                    setMetaText(e.target.value);
                    setMetaError('');
                  }}
                  placeholder={'{\n  "inventoryId": "abc-42",\n  "owner": "team-proxies"\n}'}
                  rows={6}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                {metaError && <Alert type="error" message={metaError} style={{ marginTop: 8 }} showIcon />}
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  Free-form JSON object stored as-is and returned back via the integration API. Max 16 KB.
                </Text>
              </>
            ),
          }]}
        />
        {/* Keeps Enter-to-submit working now that the visible button lives in the footer. */}
        <button type="submit" hidden aria-hidden />
      </Form>
    </Modal>
  );
};

export default ServerFormModal;
